import React, { useEffect, useState, useMemo } from 'react';
import { Plus, X, Pencil, CalendarDays, LogIn, RotateCcw } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import ConfirmDialog from '../components/ConfirmDialog';

const statusColors = {
  ACTIVE:    'bg-blue-100 text-blue-700',
  CONFIRMED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-700',
  CONVERTED: 'bg-gray-100 text-gray-600',
};

const fmtDate = d =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const emptyForm = { studentId: '', roomId: '', reservationFee: '', expectedMoveIn: '', notes: '', status: 'ACTIVE', paymentStatus: 'PENDING' };

export default function Reservations() {
  const [reservations, setReservations] = useState([]);
  const [students, setStudents]         = useState([]);
  const [rooms, setRooms]               = useState([]);
  const [showForm, setShowForm]           = useState(false);
  const [editTarget, setEditTarget]       = useState(null); // reservation being edited
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [showConvert, setShowConvert]     = useState(null); // reservation being converted
  const [confirmRevert, setConfirmRevert] = useState(null); // reservation being reverted
  const [convertForm, setConvertForm]     = useState({ startDate: '', monthlyRent: '', keyMoney: '', keyMoneyMonths: 2 });
  const [form, setForm]                   = useState(emptyForm);

  // Students who already have an active/confirmed reservation
  const reservedStudentIds = useMemo(() => new Set(
    reservations
      .filter(r => r.status === 'ACTIVE' || r.status === 'CONFIRMED')
      .map(r => r.student.id)
  ), [reservations]);

  // Rooms with at least 1 free space OR a tenant on notice (space opening soon)
  const availableRooms = useMemo(() => rooms.filter(r => {
    if (r.status === 'MAINTENANCE') return false;
    const occupied = r.tenancies?.length || 0;
    const hasFreeSpace = occupied < r.capacity;
    const hasNotice = r.tenancies?.some(t => t.status === 'NOTICE_GIVEN');
    return hasFreeSpace || hasNotice;
  }), [rooms]);

  // Human-readable option label showing free spaces
  const roomLabel = r => {
    const occupied = r.tenancies?.length || 0;
    const free = r.capacity - occupied;
    const noticeCount = r.tenancies?.filter(t => t.status === 'NOTICE_GIVEN').length || 0;
    let info = '';
    if (free > 0 && noticeCount > 0) info = `${free} free · ${noticeCount} on notice`;
    else if (free > 0)               info = `${free} free`;
    else if (noticeCount > 0)        info = 'opening soon';
    return `${r.roomNumber} — ${r.category} (${info})`;
  };

  const load = () => api.get('/reservations').then(r => setReservations(r.data)).catch(console.error);

  useEffect(() => {
    load();
    api.get('/students').then(r => setStudents(r.data));
    api.get('/rooms').then(r => setRooms(r.data));
  }, []);

  // ── Open edit modal pre-filled ──────────────────────────────────────────
  const openEdit = r => {
    setEditTarget(r);
    setForm({
      studentId:     r.student.id,
      roomId:        r.room.id,
      reservationFee: Number(r.reservationFee),
      expectedMoveIn: r.expectedMoveIn ? new Date(r.expectedMoveIn).toISOString().split('T')[0] : '',
      notes:         r.notes || '',
      status:        r.status,
      paymentStatus: r.paymentStatus,
    });
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditTarget(null); setForm(emptyForm); };

  // ── Create ──────────────────────────────────────────────────────────────
  const submit = async e => {
    e.preventDefault();
    try {
      if (editTarget) {
        // Update — only send editable fields (not studentId for existing)
        const payload = {
          roomId:         form.roomId,
          reservationFee: form.reservationFee,
          expectedMoveIn: form.expectedMoveIn || null,
          notes:          form.notes,
          status:         form.status,
          paymentStatus:  form.paymentStatus,
        };
        await api.put(`/reservations/${editTarget.id}`, payload);
        toast.success('Reservation updated');
      } else {
        await api.post('/reservations', form);
        toast.success('Reservation created');
      }
      closeForm();
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  // ── Cancel ──────────────────────────────────────────────────────────────
  const doCancel = async () => {
    try {
      await api.put(`/reservations/${confirmCancel.id}/cancel`);
      toast.success('Reservation cancelled');
      setConfirmCancel(null);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); setConfirmCancel(null); }
  };

  // ── Convert to Tenancy ──────────────────────────────────────────────────
  const openConvert = r => {
    const today = new Date().toISOString().split('T')[0];
    const rent  = Number(r.room.monthlyRent);
    setConvertForm({
      startDate:      today,
      monthlyRent:    rent,
      keyMoney:       rent * 2,   // sensible default: 2 months
      keyMoneyMonths: 2,
    });
    setShowConvert(r);
  };

  const submitConvert = async e => {
    e.preventDefault();
    try {
      await api.put(`/reservations/${showConvert.id}/convert`, convertForm);
      toast.success(`${showConvert.student.firstName} ${showConvert.student.lastName} moved in — tenancy created`);
      setShowConvert(null);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  // ── Revert conversion ────────────────────────────────────────────────────
  const doRevert = async () => {
    try {
      const { data } = await api.put(`/reservations/${confirmRevert.id}/revert`);
      toast.success(data.tenancyRemoved
        ? 'Conversion undone — tenancy deleted, reservation back to Confirmed'
        : 'Reservation reverted to Confirmed (no active tenancy found to remove)');
      setConfirmRevert(null);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); setConfirmRevert(null); }
  };

  const canEdit = r => r.status === 'ACTIVE' || r.status === 'CONFIRMED';

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800">Reservations</h2>
        <button onClick={() => { setEditTarget(null); setForm(emptyForm); setShowForm(true); }}
          className="bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
          <Plus size={16} /> New Reservation
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Student</th>
                <th className="px-4 py-3 text-left">Room</th>
                <th className="px-4 py-3 text-left">Fee</th>
                <th className="px-4 py-3 text-left">Payment</th>
                <th className="px-4 py-3 text-left">Expected Move-In</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Notes</th>
                <th className="px-4 py-3 text-left"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {reservations.map(r => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium">{r.student.firstName} {r.student.lastName}</p>
                    <p className="text-xs text-gray-400">{r.student.registrationNo}</p>
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-700">{r.room.roomNumber}</td>
                  <td className="px-4 py-3 font-semibold text-brand-700">
                    Rs. {Number(r.reservationFee).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      r.paymentStatus === 'PAID' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {r.paymentStatus}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    <span className="flex items-center gap-1">
                      {r.expectedMoveIn && <CalendarDays size={12} className="text-gray-400" />}
                      {fmtDate(r.expectedMoveIn)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[r.status]}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs max-w-[140px] truncate" title={r.notes}>
                    {r.notes || '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {canEdit(r) && (
                        <>
                          <button onClick={() => openConvert(r)}
                            title="Convert to tenancy (move in)"
                            className="text-gray-400 hover:text-green-600 transition-colors">
                            <LogIn size={15} />
                          </button>
                          <button onClick={() => openEdit(r)}
                            title="Edit reservation"
                            className="text-gray-400 hover:text-brand-600 transition-colors">
                            <Pencil size={15} />
                          </button>
                        </>
                      )}
                      {r.status === 'ACTIVE' && (
                        <button
                          onClick={() => setConfirmCancel({ id: r.id, name: `${r.student.firstName} ${r.student.lastName}`, room: r.room.roomNumber })}
                          title="Cancel reservation"
                          className="text-gray-300 hover:text-red-600 transition-colors">
                          <X size={16} />
                        </button>
                      )}
                      {r.status === 'CONVERTED' && (
                        <button
                          onClick={() => setConfirmRevert(r)}
                          title="Undo conversion — removes tenancy and reverts to Confirmed"
                          className="text-gray-300 hover:text-amber-600 transition-colors">
                          <RotateCcw size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {reservations.length === 0 && <p className="text-center text-gray-400 py-10">No reservations</p>}
        </div>
      </div>

      {/* ── Create / Edit Modal ── */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="font-bold text-lg mb-1 text-gray-800">
              {editTarget ? 'Edit Reservation' : 'New Reservation'}
            </h3>
            {editTarget && (
              <p className="text-sm text-gray-500 mb-4">
                {editTarget.student.firstName} {editTarget.student.lastName} · Room {editTarget.room.roomNumber}
              </p>
            )}

            <form onSubmit={submit} className="space-y-3">
              {/* Student — only shown when creating */}
              {!editTarget && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Student *</label>
                  <select required value={form.studentId}
                    onChange={e => setForm(p => ({ ...p, studentId: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                    <option value="">Select student</option>
                    {students
                      .filter(s => s.status === 'ACTIVE' && !s.tenancies?.length && !reservedStudentIds.has(s.id))
                      .map(s => (
                        <option key={s.id} value={s.id}>{s.firstName} {s.lastName} ({s.registrationNo})</option>
                      ))}
                  </select>
                </div>
              )}

              {/* Room */}
              <div>
                <label className="text-sm font-medium text-gray-700">Room *</label>
                <select required value={form.roomId}
                  onChange={e => setForm(p => ({ ...p, roomId: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                  <option value="">Select room</option>
                  {(editTarget ? rooms : availableRooms).map(r => (
                    <option key={r.id} value={r.id}>
                      {editTarget ? `${r.roomNumber} — ${r.category} (${r.status})` : roomLabel(r)}
                    </option>
                  ))}
                </select>
                {!editTarget && availableRooms.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">No rooms with free spaces currently.</p>
                )}
              </div>

              {/* Fee + Payment status */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Reservation Fee (Rs.) *</label>
                  <input type="number" required value={form.reservationFee}
                    onChange={e => setForm(p => ({ ...p, reservationFee: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Fee Payment</label>
                  <select value={form.paymentStatus}
                    onChange={e => setForm(p => ({ ...p, paymentStatus: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                    <option value="PENDING">Pending</option>
                    <option value="PAID">Paid</option>
                  </select>
                </div>
              </div>

              {/* Expected Move-In + Status */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Expected Move-In</label>
                  <input type="date" value={form.expectedMoveIn}
                    onChange={e => setForm(p => ({ ...p, expectedMoveIn: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
                </div>
                {editTarget && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Status</label>
                    <select value={form.status}
                      onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                      <option value="ACTIVE">Active</option>
                      <option value="CONFIRMED">Confirmed</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="text-sm font-medium text-gray-700">Notes</label>
                <textarea value={form.notes}
                  onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2}
                  className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeForm}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit"
                  className="flex-1 bg-brand-700 hover:bg-brand-800 text-white py-2 rounded-lg text-sm font-medium">
                  {editTarget ? 'Save Changes' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Convert to Tenancy Modal ── */}
      {showConvert && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <LogIn size={16} className="text-green-700" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-gray-800">Convert to Tenancy</h3>
                <p className="text-sm text-gray-500">
                  {showConvert.student.firstName} {showConvert.student.lastName} · Room {showConvert.room.roomNumber}
                </p>
              </div>
            </div>

            {showConvert.expectedMoveIn && (
              <div className="bg-blue-50 rounded-lg px-3 py-2 text-xs text-blue-700 mt-3 mb-1 flex items-center gap-1.5">
                <CalendarDays size={12} />
                Expected move-in was {new Date(showConvert.expectedMoveIn).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
              </div>
            )}

            <form onSubmit={submitConvert} className="space-y-3 mt-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Actual Move-In Date *</label>
                <input type="date" required value={convertForm.startDate}
                  onChange={e => setConvertForm(p => ({ ...p, startDate: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Monthly Rent (Rs.) *</label>
                  <input type="number" required value={convertForm.monthlyRent}
                    onChange={e => setConvertForm(p => ({ ...p, monthlyRent: e.target.value, keyMoney: Number(e.target.value) * Number(p.keyMoneyMonths) }))}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Key Money Months</label>
                  <input type="number" min={1} value={convertForm.keyMoneyMonths}
                    onChange={e => setConvertForm(p => ({ ...p, keyMoneyMonths: e.target.value, keyMoney: Number(p.monthlyRent) * Number(e.target.value) }))}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Key Money (Rs.) *</label>
                <input type="number" required value={convertForm.keyMoney}
                  onChange={e => setConvertForm(p => ({ ...p, keyMoney: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowConvert(null)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit"
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2">
                  <LogIn size={15} /> Confirm Move-In
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cancel confirm */}
      <ConfirmDialog
        open={!!confirmCancel}
        title="Cancel Reservation"
        message={confirmCancel ? `Cancel the reservation for ${confirmCancel.name} in Room ${confirmCancel.room}?` : ''}
        confirmLabel="Yes, Cancel"
        onConfirm={doCancel}
        onCancel={() => setConfirmCancel(null)}
      />

      {/* Revert confirm */}
      <ConfirmDialog
        open={!!confirmRevert}
        title="Undo Conversion"
        message={confirmRevert
          ? `This will delete the tenancy created for ${confirmRevert.student.firstName} ${confirmRevert.student.lastName} in Room ${confirmRevert.room.roomNumber} (including any payments recorded) and revert the reservation back to Confirmed.`
          : ''}
        confirmLabel="Yes, Undo"
        onConfirm={doRevert}
        onCancel={() => setConfirmRevert(null)}
      />
    </div>
  );
}
